/**
 * Common filter options used across different filter types
 * These options are reusable and can be shared between different filters
 */
import { getStaticFilterData } from "@shared/config/filter-static-data"
export const commonOptions = {
  currency: [
    { value: "all", label: "All Currencies", category: "all" },
    { value: "USD", label: "USD", category: "major", symbol: "$" },
    { value: "EUR", label: "EUR", category: "major", symbol: "€" },
    { value: "GBP", label: "GBP", category: "major", symbol: "£" },
    { value: "BGN", label: "BGN", category: "regional", symbol: "лв" },
    { value: "CAD", label: "CAD", category: "major", symbol: "C$" },
    { value: "AUD", label: "AUD", category: "major", symbol: "A$" },
    { value: "JPY", label: "JPY", category: "major", symbol: "¥" },
    { value: "CHF", label: "CHF", category: "regional", symbol: "CHF" },
  ],
  paymentMethod: [
    { value: "visa", label: "Visa", category: "card" },
    { value: "mastercard", label: "Mastercard", category: "card" },
    { value: "amex", label: "American Express", category: "card" },
    { value: "discover", label: "Discover", category: "card" },
    { value: "unionpay", label: "UnionPay", category: "card" },
    { value: "apple_pay", label: "Apple Pay", category: "wallet" },
    { value: "google_pay", label: "Google Pay", category: "wallet" },
    { value: "paypal", label: "PayPal", category: "wallet" },
    { value: "ach", label: "ACH", category: "banking" },
    { value: "sepa", label: "SEPA", category: "banking" },
    { value: "wire", label: "Wire Transfer", category: "banking" },
    { value: "bank_transfer", label: "Bank Transfer", category: "banking" },
    { value: "klarna", label: "Klarna", category: "bnpl" },
    { value: "afterpay", label: "Afterpay", category: "bnpl" },
    { value: "affirm", label: "Affirm", category: "bnpl" },
  ],
  country: [
    { value: "all", label: "All Countries", region: "all" },
    { value: "US", label: "United States", region: "americas", code: "US" },
    { value: "CA", label: "Canada", region: "americas", code: "CA" },
    { value: "GB", label: "United Kingdom", region: "europe", code: "GB" },
    { value: "DE", label: "Germany", region: "europe", code: "DE" },
    { value: "FR", label: "France", region: "europe", code: "FR" },
    { value: "AU", label: "Australia", region: "apac", code: "AU" },
    { value: "JP", label: "Japan", region: "apac", code: "JP" },
  ],
  channel: [
    { value: "online", label: "Online", icon: "Globe" },
    { value: "mobile", label: "Mobile", icon: "Smartphone" },
    { value: "pos", label: "Point of Sale", icon: "CreditCard" },
    { value: "api", label: "API", icon: "Code" },
  ],
  programType: [
    { value: "all", label: "All Types", category: "all" },
    { value: "loyalty", label: "Loyalty", category: "customer" },
    { value: "rewards", label: "Rewards", category: "customer" },
    { value: "cashback", label: "Cashback", category: "financial" },
    { value: "points", label: "Points", category: "customer" },
  ],
  terminalStore: getStaticFilterData('stores'),
  terminalLocation: getStaticFilterData('locations'),
  terminalType: [
    { value: "all", label: "All Types" },
    { value: "countertop", label: "Countertop" },
    { value: "mobile", label: "Mobile" },
    { value: "portable", label: "Portable" },
    { value: "handheld", label: "Handheld" },
    { value: "fixed", label: "Fixed" },
    { value: "kiosk", label: "Kiosk" },
  ],
  organization: [
    { value: "all", label: "All Organizations" },
    { value: "org_alpha", label: "Org Alpha" },
    { value: "org_beta", label: "Org Beta" },
    { value: "org_gamma", label: "Org Gamma" },
  ],
  productPlatform: [
    { value: "all", label: "All Product Platforms" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "pos", label: "POS" },
    { value: "mobile", label: "Mobile" },
    { value: "saas", label: "SaaS" },
  ],
  processor: [
    { value: "all", label: "All Processors" },
    { value: "stripe", label: "Stripe" },
    { value: "adyen", label: "Adyen" },
    { value: "worldpay", label: "Worldpay" },
    { value: "checkout", label: "Checkout.com" },
    { value: "paypal", label: "PayPal" },
  ],
  bank: [
    { value: "all", label: "All Banks" },
    { value: "jpmorgan", label: "J.P. Morgan" },
    { value: "bank_of_america", label: "Bank of America" },
    { value: "wells_fargo", label: "Wells Fargo" },
    { value: "citi", label: "Citi" },
    { value: "hsbc", label: "HSBC" },
  ],
  ecommerceType: [
    { value: "hosted_checkout", label: "Hosted Checkout" },
    { value: "api", label: "API Integration" },
    { value: "elements", label: "Drop-In Elements" },
    { value: "drops", label: "Drops" },
  ],
  paymentLinkType: [
    { value: "one-time", label: "One-time" },
    { value: "subscription", label: "Subscription" },
    { value: "donation", label: "Donation" },
  ],
  productCategory: [
    { value: "subscription", label: "Subscription" },
    { value: "add-on",       label: "Add-on" },
    { value: "tool",         label: "Tool" },
    { value: "support",      label: "Support" },
    { value: "service",      label: "Service" },
    { value: "template",     label: "Template" },
    { value: "marketing",    label: "Marketing" },
    { value: "integration",  label: "Integration" },
    { value: "platform",     label: "Platform" },
    { value: "monitoring",   label: "Monitoring" },
    { value: "ai",           label: "AI" },
    { value: "storage",      label: "Storage" },
    { value: "automation",   label: "Automation" },
    { value: "management",   label: "Management" },
    { value: "communication",label: "Communication" },
    { value: "finance",      label: "Finance" },
    { value: "scheduling",   label: "Scheduling" },
    { value: "program",      label: "Program" },
    { value: "research",     label: "Research" },
  ],
  productType: [
    { value: "retail",  label: "Retail" },
    { value: "service", label: "Service" },
  ],
}
